import type { RequestTypeEnum } from "@nlc/shared-types";
import type { Control, FieldPath, FieldValues } from "react-hook-form";

import { HelpCircleIcon } from "lucide-react";
import { Controller } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import { FieldError, Label, TextArea, TextField } from "@/components/field";
import { Tooltip, TooltipTrigger } from "@/components/tooltip";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";

interface FormFieldProps<T extends FieldValues> {
  control: Control<T>;
  isDisabled?: boolean;
  label: string;
  name: FieldPath<T>;
  rows?: number;
  tooltip: string;
}

function FormField<T extends FieldValues>({
  control,
  isDisabled = false,
  label,
  name,
  rows = 5,
  tooltip,
}: FormFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { name: fieldName, onBlur, onChange, ref, value },
        fieldState: { error, invalid },
      }) => (
        <TextField
          isDisabled={isDisabled}
          isInvalid={invalid}
          name={fieldName}
          onBlur={onBlur}
          onChange={onChange}
          validationBehavior="aria"
          value={value}
        >
          <div className="flex items-center justify-between">
            <Label>{label}</Label>
            <TooltipTrigger delay={0}>
              <Button
                aria-label={`${label} help`}
                className="h-min cursor-default"
                variant="unstyled"
              >
                <HelpCircleIcon className="text-fg-quinary" />
              </Button>
              <Tooltip offset={0} placement="right">
                {tooltip}
              </Tooltip>
            </TooltipTrigger>
          </div>
          <TextArea className="resize-none" ref={ref} rows={rows} />
          <div className="mt-0 flex items-start justify-between">
            <div className="-mt-sm flex-1">
              <FieldError className="-mt-0">{error?.message}</FieldError>
            </div>
            <span
              className={twMerge(
                "ml-4 shrink-0 text-sm text-text-tertiary",
                error && "text-text-error-primary",
              )}
            >{`${value?.length || 0}/800`}</span>
          </div>
        </TextField>
      )}
    />
  );
}

export function OriginalInputFields() {
  const methods = useEditRequestFormContext();
  const { data: request, isLoading } = useGetRequest();

  if (isLoading) return <div>Loading...</div>;

  if (!request?.requestType) return <div>No request found</div>;
  const requestType = request?.requestType as RequestTypeEnum;

  const isDisabled = true;

  return (
    <div className="col-span-1 row-span-1 flex flex-col gap-3xl">
      <div className="flex flex-col gap-3xl">
        <FormField
          control={methods.control}
          isDisabled={isDisabled}
          label="🎯 Scope"
          name="magicalBriefCreator.scope"
          rows={5}
          tooltip="Outlines the project's primary objectives and overall aims, setting the foundation for expected business or marketing outcomes."
        />

        <FormField
          control={methods.control}
          isDisabled={isDisabled}
          label="🌈 Creative goal"
          name="magicalBriefCreator.creativeGoals"
          rows={5}
          tooltip="Specifies the project's artistic and stylistic approach, including visual intent, brand guidelines adherence, and creative inspirations."
        />
      </div>
      {requestType === "video" ? (
        <FormField
          control={methods.control}
          isDisabled={isDisabled}
          label="💬 Script"
          name="magicalBriefCreator.script"
          rows={5}
          tooltip="Scene 1: Close-up of product in natural setting. Scene 2: Demonstration of product application. Scene 3: Time-lapse showing visible skin improvement. Scene 4: Highlight key organic ingredients. Scene 5: Call-to-action with brand logo"
        />
      ) : (
        <FormField
          control={methods.control}
          isDisabled={isDisabled}
          label={`💬  ${
            requestType === "graphic" ? "Key Message & CTA" : "Text for design"
          }`}
          name={
            requestType === "graphic"
              ? "magicalBriefCreator.keyMessageAndCta"
              : "textForDesign"
          }
          rows={5}
          tooltip={
            requestType === "graphic"
              ? "Specifies exact text and slogans for the design, including a clear call to action (CTA) to prompt audience engagement."
              : "e.g. Design vibrant and minimalist graphics that highlight the product's natural ingredients."
          }
        />
      )}
      <FormField
        control={methods.control}
        isDisabled={isDisabled}
        label="📘 Brand Guidelines"
        name="magicalBriefCreator.brandGuidelines"
        rows={3}
        tooltip="Specify which elements of your selected brand to use in this design. Indicate any preferences for fonts, colors, logos, or other brand elements, and mention any exclusions if certain aspects should not be incorporated."
      />

      <FormField
        control={methods.control}
        isDisabled={isDisabled}
        label="✏️ Additional Guidelines"
        name="magicalBriefCreator.additionalGuidelines"
        rows={3}
        tooltip="Provides further instructions or specific details not covered elsewhere, such as legal disclaimers, language use, and design constraints"
      />
    </div>
  );
}
